import {
  get_entity_type_name,
  is_open_case_status,
  normalize_text,
  read_session_project_id,
  to_number,
} from '../company_cases_helpers';
import {
  fetch_coordinator_assigned_company_cases,
  fetch_closed_case_summary,
} from '../company_cases_live_queue';
import {
  fetch_scoped_entity_types,
} from '../company_cases_response_helpers';
import BlockingLoaderOverlay from '@/components/config_components/BlockingLoaderOverlay/BlockingLoaderOverlay.vue';
import { create_requestor_case } from '@/services/requestor_portal_service';
import { GET_USERS, get_request_config } from '@/utils/system_constants/constant.js';
import axios from 'axios';
import { response_items } from '../../PanelRegister/panel_register_helpers';
import { map_panel_member } from '../../PanelRegister/panel_register_member_helpers';
import {
  created_manual_case,
  resolve_manual_case_form_ids,
  validate_manual_case_registration,
} from '../../../IntakeOfficer/manual_case_form_helpers';

const OPEN_SCOPE_REGISTER_MODAL_EVENT = 'x-verdict:open-scope-register-modal';

function create_scope_dashboard_state() {
  return {
    loading_scope_cases: false,
    coordinator_scope: null,
    metrics: [
      { label: 'Assigned', value: '-', change: 'Live coordinator queue', change_class: 'secondary-color' },
      { label: 'Panel', value: '12', change: '47 handlers active', change_class: 'text-secondary' },
      { label: 'SLA At Risk', value: '-', change: '7-day window', change_class: 'text-secondary' },
      { label: 'Closed (Q)', value: '312', change: 'Up 8% vs prev', change_class: 'secondary-color' }
    ],
    scope_cases: [],
    show_manual_case_modal: false,
    manual_case: manual_case_form(),
    case_type_options: [],
    submitting_manual_case: false,
    manual_case_error: '',
  };
}

function manual_case_form() {
  return {
    title: 'Register · inbound request',
    subtitle: 'Manual capture · Just now',
    description: '',
    requester: '',
    contact: '',
    location: '',
    case_type: '',
  };
}

function build_panel_summary(members = []) {
  return {
    panel_count: members.length,
    active_count: members.filter((member) => member.is_active).length,
  };
}

async function fetch_panel_summary(project_id = null) {
  const response = await axios.get(
    GET_USERS,
    get_request_config({
      params: {
        page: 1,
        page_size: 500,
        ...(project_id ? { project_id } : {}),
      },
    })
  );

  const members = response_items(response.data)
    .map((profile, index) => map_panel_member(profile, index, []))
    .filter((member) => member.id != null);

  return build_panel_summary(members);
}

function route_project_id(route) {
  return to_number(
    route?.query?.project_id
    || route?.query?.projectId
    || read_session_project_id()
  );
}

function metric_value(value) {
  return Number(value || 0).toLocaleString('en-ZA');
}

function closed_change_text(summary = {}) {
  if (summary.change_percentage === null) {
    return 'Current quarter';
  }

  if (summary.change_percentage > 0) {
    return `Up ${summary.change_percentage}% vs prev`;
  }

  if (summary.change_percentage < 0) {
    return `Down ${Math.abs(summary.change_percentage)}% vs prev`;
  }

  return 'Flat vs prev';
}

function is_sla_at_risk(case_item = {}) {
  return case_item.sla_class?.includes('text-warning') || case_item.sla_class?.includes('text-danger');
}

function format_scope_case(case_item = {}) {
  return {
    application_id: case_item.application_id || null,
    entity_profile_id: case_item.entity_profile_id || null,
    entity_type_id: case_item.entity_type_id || null,
    case_code: case_item.case_code || case_item.title || '',
    title: case_item.title || case_item.case_code || 'Untitled case',
    subtitle: case_item.subtitle || case_item.case_code || '',
    meta: case_item.subtitle || case_item.case_code,
    domain: case_item.domain || 'Case',
    domain_badge_class: case_item.domain_badge_class || 'bg-light text-secondary',
    panel_member: case_item.owner || 'Unassigned',
    risk_score: case_item.risk_score || '0',
    risk_label: case_item.risk_label || 'Open',
    risk_badge_class: case_item.risk_badge_class || 'bg-light text-secondary',
    sla: case_item.sla || 'Queued',
    sla_class: case_item.sla_class || 'text-secondary',
    sla_icon: case_item.sla_icon || ''
  };
}

function case_detail_query(case_item = {}) {
  return Object.fromEntries([
    ['application_id', case_item?.application_id],
    ['entity_profile_id', case_item?.entity_profile_id],
    ['entity_type_id', case_item?.entity_type_id],
  ].filter(([, value]) => Boolean(value)));
}

function weekly_scope_change(open_cases = []) {
  return open_cases.length === 1 ? '1 open case routed to you' : 'Open cases routed to you';
}

export default {
  name: 'ScopeDashboard',
  components: {
    BlockingLoaderOverlay,
  },
  data: () => create_scope_dashboard_state(),
  computed: {
    current_project_id() {
      return route_project_id(this.$route);
    },
    current_user_name() {
      return normalize_text(
        this.$store?.getters?.currentLeadName
        || sessionStorage.getItem('current_lead_name')
        || sessionStorage.getItem('lead_name')
      );
    },
    scope_banner_title() {
      return 'Assigned case queue';
    },
    scope_banner_copy() {
      const assignee = this.current_user_name || 'you';
      return `Open cases currently routed to ${assignee} for coordination.`;
    },
    page_loading_message() {
      return 'Loading scope dashboard...';
    },
    page_loading_detail() {
      return 'Fetching your scoped cases, panel coverage, and dashboard metrics.';
    }
  },
  watch: {
    current_project_id: {
      immediate: true,
      handler() {
        this.load_scope_dashboard();
        this.load_case_type_options();
      }
    }
  },
  mounted() {
    if (typeof window !== 'undefined') {
      window.addEventListener(OPEN_SCOPE_REGISTER_MODAL_EVENT, this.open_register_case);
    }
  },
  beforeUnmount() {
    if (typeof window !== 'undefined') {
      window.removeEventListener(OPEN_SCOPE_REGISTER_MODAL_EVENT, this.open_register_case);
    }
  },
  methods: {
    open_register_case() {
      this.manual_case = manual_case_form();
      if (!this.manual_case.case_type && this.case_type_options[0]?.id) {
        this.manual_case.case_type = String(this.case_type_options[0].id);
      }
      this.show_manual_case_modal = true;
      this.manual_case_error = '';
    },
    close_manual_case_modal() {
      if (this.submitting_manual_case) return;
      this.dismiss_manual_case_modal();
    },
    dismiss_manual_case_modal() {
      this.show_manual_case_modal = false;
      this.manual_case_error = '';
      this.manual_case = manual_case_form();
      if (this.case_type_options[0]?.id) {
        this.manual_case.case_type = String(this.case_type_options[0].id);
      }
    },
    selected_manual_case_type() {
      return this.case_type_options.find(
        (option) => String(option.id) === String(this.manual_case.case_type)
      ) || null;
    },
    can_submit_manual_case() {
      return !validate_manual_case_registration(this.manual_case, this.selected_manual_case_type())
        && !this.submitting_manual_case;
    },
    async load_case_type_options() {
      try {
        const { entity_types } = await fetch_scoped_entity_types(this.current_project_id);
        this.case_type_options = entity_types
          .map((entity_type) => ({
            id: String(entity_type?.id || ''),
            label: get_entity_type_name(entity_type),
            form_ids: Array.isArray(entity_type?.form_ids) ? entity_type.form_ids : [],
          }))
          .filter((option) => option.id && option.label);

        if (!this.manual_case.case_type && this.case_type_options[0]?.id) {
          this.manual_case.case_type = String(this.case_type_options[0].id);
        }
      } catch {
        this.case_type_options = [];
      }
    },
    async submit_manual_case() {
      if (this.submitting_manual_case) return;

      const selected_case_type = this.case_type_options.find(
        (option) => String(option.id) === String(this.manual_case.case_type)
      );
      const entity_type_id = to_number(selected_case_type?.id);
      const entity_name = normalize_text(this.manual_case.description || this.manual_case.requester);

      const validation_error = validate_manual_case_registration(this.manual_case, selected_case_type);
      if (validation_error) {
        this.manual_case_error = validation_error;
        return;
      }

      this.submitting_manual_case = true;
      this.manual_case_error = '';
      try {
        const submitted_case = { ...this.manual_case };
        const form_ids = await resolve_manual_case_form_ids({
          entity_type_id,
          project_id: this.current_project_id,
          fallback_form_ids: selected_case_type.form_ids,
        });
        const create_data = await create_requestor_case({
          entity_name,
          entity_type_id,
          form_ids,
          run_onboarding: true,
          source_channel: 'Walk-in',
          user_email: is_email(this.manual_case.contact) ? this.manual_case.contact : undefined,
          send_user_email: false,
          ...(this.current_project_id ? { project_id: Number(this.current_project_id) } : {}),
        });
        const created_case = created_manual_case(create_data, entity_type_id);
        const created_row = format_scope_case({
          ...created_case,
          title: entity_name,
          subtitle: submitted_case.location || 'Manually registered',
          domain: selected_case_type?.label || 'Case',
          owner: 'Unassigned',
          sla: 'Queued',
        });

        this.dismiss_manual_case_modal();
        await this.load_scope_dashboard();
        if (!this.scope_cases.some((case_item) =>
          Number(case_item.application_id) === Number(created_row.application_id)
        )) {
          this.scope_cases = [created_row, ...this.scope_cases];
        }
      } catch (error) {
        this.manual_case_error = error?.response?.data?.message
          || error?.message
          || 'Failed to register the case.';
      } finally {
        this.submitting_manual_case = false;
      }
    },
    go_to_case_detail(case_item = {}) {
      const case_code = typeof case_item === 'string'
        ? case_item
        : case_item?.case_code || this.extract_case_code(case_item?.meta);
      this.$router.push({
        name: 'ViewCaseDetails',
        params: { caseCode: case_code },
        query: case_detail_query(case_item),
      });
    },
    extract_case_code(meta) {
      const match = String(meta || '').match(/C-\d{4}-\d{4}/);
      return match ? match[0] : '';
    },
    update_metrics(open_cases = [], closed_summary = {}, panel_summary = {}) {
      const sla_at_risk = open_cases.filter(is_sla_at_risk).length;
      this.metrics = this.metrics.map((metric) => {
        if (metric.label === 'Assigned') {
          return {
            ...metric,
            value: metric_value(open_cases.length),
            change: weekly_scope_change(open_cases),
          };
        }

        if (metric.label === 'Panel') {
          return {
            ...metric,
            value: metric_value(panel_summary.panel_count),
            change: `${metric_value(panel_summary.active_count)} handlers active`,
          };
        }

        if (metric.label === 'SLA At Risk') {
          return {
            ...metric,
            value: metric_value(sla_at_risk),
            change: '7-day window',
          };
        }

        if (metric.label === 'Closed (Q)') {
          return {
            ...metric,
            value: metric_value(closed_summary.current_quarter_count),
            change: closed_change_text(closed_summary),
          };
        }

        return metric;
      });
    },
    async load_scope_dashboard() {
      this.loading_scope_cases = true;
      try {
        const [{ coordinator_scope }, assigned_cases, closed_summary, panel_summary] = await Promise.all([
          fetch_scoped_entity_types(this.current_project_id),
          fetch_coordinator_assigned_company_cases(this.current_project_id),
          fetch_closed_case_summary(this.current_project_id),
          fetch_panel_summary(this.current_project_id),
        ]);
        const open_cases = assigned_cases.filter((case_item) => is_open_case_status(case_item.status));
        this.coordinator_scope = coordinator_scope;
        this.update_metrics(open_cases, closed_summary, panel_summary);
        this.scope_cases = open_cases.slice(0, 10).map(format_scope_case);
      } catch {
        this.coordinator_scope = null;
        this.update_metrics([], { current_quarter_count: 0, change_percentage: null }, { panel_count: 0, active_count: 0 });
        this.scope_cases = [];
      } finally {
        this.loading_scope_cases = false;
      }
    }
  }
};

function is_email(value = '') {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
}

export {
  OPEN_SCOPE_REGISTER_MODAL_EVENT,
};
